const API_URL = process.env.REACT_APP_API_URL;

// Hilfsfunktion für 401-Behandlung
function handleUnauthorized(response: Response) {
  if (response.status === 401) {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    window.dispatchEvent(new CustomEvent("auth:logout"));
  }
}

export type AssignedPlayer = {
  sn: string;
  location: string;
  time_zone?: string;
};

export type User = {
  id: number;
  username: string;
  role: "admin" | "user";
  customerName?: string;
  customerId?: string;
  assigned_players: AssignedPlayer[];
};

export type LoginResponse = {
  token: string;
  user: User;
  /** Einmaliger Token für Player-Bootstrap (nur bei Login mit player_sn). */
  bootstrapToken?: string;
};

export type Content = {
  file_name: string;
  content_name: string;
  created_at: string;
  sha1?: string | null;
  sha1_encrypted?: string | null;
};

// NEU: Typ-Definition für einen Zeitplaneintrag
export type ScheduleEntry = {
  id: number; // SERIAL PRIMARY KEY im Backend
  player_sn: string;
  day_of_week: number; // 1=Montag, ..., 7=Sonntag
  start_time: string; // TIME WITH TIME ZONE (Format: "HH:MM:SS+HH:MM" oder "HH:MM:SS-HH:MM")
  feed_id: string; // UUID
  feed_external_name: string;
  specific_date?: string | null; // Optional: Datum im Format YYYY-MM-DD für spezielle Datumsplanung
  created_at: string; // Timestamp
};


async function apiFetch(path: string, options: RequestInit = {}) {
  const token = localStorage.getItem("token");
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (options.headers && typeof options.headers === "object" && !Array.isArray(options.headers)) {
    Object.assign(headers, options.headers);
  }

  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_URL}${path}`, { ...options, headers });

  if (!res.ok) {
    // Bei 401-Fehler (Unauthorized) automatisch ausloggen
    handleUnauthorized(res);
    
    let errorData: any;
    const contentType = res.headers.get("content-type");
    try {
      // Versuche zuerst, den Body als Text zu lesen (funktioniert immer)
      const text = await res.text();
      // Versuche dann, den Text als JSON zu parsen, wenn Content-Type JSON ist
      if (contentType && contentType.includes("application/json")) {
        try {
          errorData = JSON.parse(text);
        } catch {
          errorData = { message: text || res.statusText };
        }
      } else {
        errorData = text || { message: res.statusText };
      }
    } catch (e) {
      // Wenn das Lesen fehlschlägt, verwende Status-Text
      errorData = { message: res.statusText };
    }
    throw { status: res.status, data: errorData, message: errorData.message || res.statusText };
  }
  if (res.status === 204) return null;
  return res.json();
}

/* ---------------------------------------------------------- */
// Zugriff auf Endpunkte des VPS (node.js)
/* ---------------------------------------------------------- */

/* ---------------------------------------------------------- */
// Authentifizierung
/* ---------------------------------------------------------- */
export async function login(
  username: string,
  password: string,
  playerSn?: string | null
): Promise<LoginResponse> {
  const body: { username: string; password: string; player_sn?: string } = { username, password };
  if (playerSn != null && playerSn.trim()) body.player_sn = playerSn.trim();
  const res = await apiFetch("/login", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return res;
}

/* ---------------------------------------------------------- */
// Players
/* ---------------------------------------------------------- */
export async function fetchPlayersByCustomer(customerId: string) {
  return apiFetch(`/customers/${customerId}/players`);
}

export async function createPlayer(customerId: string, sn: string, location: string, time_zone: string) {
  return apiFetch(`/customers/${customerId}/players`, {
    method: "POST",
    body: JSON.stringify({ sn, location, time_zone }),
  });
}

export async function updatePlayer(customerId: string, playerSN: string, location?: string, time_zone?: string) {
  return apiFetch(`/customers/${customerId}/players/${playerSN}`, {
    method: "PATCH",
    body: JSON.stringify({ location, time_zone }),
  });
}

export async function deletePlayer(customerId: string, playerSN: string) {
  return apiFetch(`/customers/${customerId}/players/${playerSN}`, {
    method: "DELETE",
  });
}

export async function fetchPlayersForUser(userId: number) {
  return apiFetch(`/users/${userId}/players`);
}

export type PlayerWithCustomer = {
  sn: string;
  location: string;
  customer_name: string;
};

export async function fetchAllPlayersWithCustomers(): Promise<PlayerWithCustomer[]> {
  return apiFetch(`/players-with-customers`);
}


/* ---------------------------------------------------------- */
// Content
/* ---------------------------------------------------------- */
export async function fetchAllowedContent(playerSN: string) {
  const res = await apiFetch(`/allowed/${playerSN}`);
  return res.allowed || [];
}

export async function fetchAllContent(): Promise<Content[]> {
  return apiFetch(`/contents`);
}

export async function updateAllowedContentForPlayer(playerSN: string, contentNames: string[]) {
  return apiFetch(`/players/${playerSN}/allowed-content`, {
    method: "PATCH",
    body: JSON.stringify({ contentNames }),
  });
}

// Fehlende SHA1-/sha1_encrypted-Hashes für Inhalte berechnen
export async function calculateMissingHashes(fileNames: string[]): Promise<{
  success: boolean;
  results: Array<{ file_name: string; sha1: string; sha1_encrypted?: string; status: 'calculated' | 'already_exists' }>;
  errors?: Array<{ file_name: string; error: string }>;
  summary: {
    total: number;
    calculated: number;
    already_exists: number;
    errors: number;
  };
}> {
  return apiFetch(`/contents/calculate-missing-hashes`, {
    method: "POST",
    body: JSON.stringify({ file_names: fileNames }),
  });
}


/* ---------------------------------------------------------- */
// Feeds
/* ---------------------------------------------------------- */
export async function fetchFeeds(playerSN: string) {
  return apiFetch(`/players/${playerSN}/feeds`);
}

export async function createFeed(playerSN: string, externalName: string, contentIds: string[] = []) {
  return apiFetch(`/players/${playerSN}/feeds`, {
    method: "POST",
    body: JSON.stringify({ externalName, contentIds }),
  });
}

export async function updateFeedContents(
  playerSN: string, 
  feedId: string, 
  contentIds: string[], 
  contentMetadata?: { [fileName: string]: { display_duration?: number } }
) {
  return apiFetch(`/players/${playerSN}/feeds/${feedId}/contents`, {
    method: "PATCH",
    body: JSON.stringify({ contentIds, contentMetadata }),
  });
}

export async function renameFeed(playerSN: string, feedId: string, externalName: string) {
  return apiFetch(`/players/${playerSN}/feeds/${feedId}/rename`, {
    method: "PATCH",
    body: JSON.stringify({ externalName }),
  });
}

export async function deleteFeed(playerSN: string, feedId: string) {
  return apiFetch(`/players/${playerSN}/feeds/${feedId}`, {
    method: "DELETE",
  });
}

export async function startFeed(playerId: string, feedId: string, externalName: string) {
  const res = await fetch(`${API_URL}/startFeed`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ playerId, feedId, externalName }),
  });
  if (!res.ok) {
    handleUnauthorized(res);
    let errorBody: any;
    try {
      errorBody = await res.json();
    } catch (e) {
      errorBody = await res.text();
    }
    throw errorBody;
  }
  return res.json();
}

/* ---------------------------------------------------------- */
// Customers
/* ---------------------------------------------------------- */
export async function fetchCustomers() {
  return apiFetch("/customers");
}

export async function fetchAllCustomers() {
  return apiFetch("/customers");
}

export async function createCustomer(name: string) {
  return apiFetch("/customers", {
    method: "POST",
    body: JSON.stringify({ name }),
  });
}

export async function deleteCustomer(customerId: string) {
  return apiFetch(`/customers/${customerId}`, {
    method: "DELETE",
  });
}

export async function renameCustomer(customerId: string, name: string) {
  return apiFetch(`/customers/${customerId}`, {
    method: "PATCH",
    body: JSON.stringify({ name }),
  });
}

/* ---------------------------------------------------------- */
// Customer Day Parts (Tageszeiten) pro Kunde
/* ---------------------------------------------------------- */
export type CustomerDayPart = {
  id: number;
  name: string;
  customer_id: string;
  from_time: string;
  created_at?: string;
  create_user?: number | null;
  updated_at?: string | null;
  update_user?: number | null;
};

export async function fetchDayParts(customerId: string): Promise<CustomerDayPart[]> {
  return apiFetch(`/customers/${customerId}/day-parts`);
}

export async function createDayPart(customerId: string, data: { name: string; from_time: string }) {
  return apiFetch(`/customers/${customerId}/day-parts`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateDayPart(
  customerId: string,
  dayPartId: number,
  data: { name?: string; from_time?: string }
) {
  return apiFetch(`/customers/${customerId}/day-parts/${dayPartId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteDayPart(customerId: string, dayPartId: number) {
  return apiFetch(`/customers/${customerId}/day-parts/${dayPartId}`, {
    method: "DELETE",
  });
}

/* ---------------------------------------------------------- */
// Ad-Slot-Policies (Werbe-Slot Richtlinie) pro Kunde
/* ---------------------------------------------------------- */
export type AdSlotPolicy = {
  id: number;
  name: string;
  customer_id: string;
  max_ad_time_per_hour: number | null;
  max_ads_per_break: number | null;
  max_break_duration: number | null;
  min_gap_between_breaks: number | null;
  preferred_interval: number | null;
  target_ad_ratio: number | null;
  midroll_mode: "none" | "breakpoint" | "anytime";
  postroll_mode: "none" | "active";
  day_part: number | null;
  is_default: boolean;
  created_at?: string;
  create_user?: number | null;
  updated_at?: string | null;
  update_user?: number | null;
};

export async function fetchAdSlotPolicies(customerId: string): Promise<AdSlotPolicy[]> {
  return apiFetch(`/customers/${customerId}/ad-slot-policies`);
}

export async function createAdSlotPolicy(
  customerId: string,
  data: {
    name: string;
    max_ad_time_per_hour?: number | null;
    max_ads_per_break?: number | null;
    max_break_duration?: number | null;
    min_gap_between_breaks?: number | null;
    preferred_interval?: number | null;
    target_ad_ratio?: number | null;
    midroll_mode?: "none" | "breakpoint" | "anytime";
    postroll_mode?: "none" | "active";
    day_part?: number | null;
    is_default?: boolean;
  }
) {
  return apiFetch(`/customers/${customerId}/ad-slot-policies`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateAdSlotPolicy(
  customerId: string,
  policyId: number,
  data: Partial<{
    name: string;
    max_ad_time_per_hour: number | null;
    max_ads_per_break: number | null;
    max_break_duration: number | null;
    min_gap_between_breaks: number | null;
    preferred_interval: number | null;
    target_ad_ratio: number | null;
    midroll_mode: "none" | "breakpoint" | "anytime";
    postroll_mode: "none" | "active";
    day_part: number | null;
    is_default: boolean;
  }>
) {
  return apiFetch(`/customers/${customerId}/ad-slot-policies/${policyId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteAdSlotPolicy(customerId: string, policyId: number) {
  return apiFetch(`/customers/${customerId}/ad-slot-policies/${policyId}`, {
    method: "DELETE",
  });
}

/* ---------------------------------------------------------- */
// Advertisers (Admin)
/* ---------------------------------------------------------- */
export type Advertiser = {
  id: number;
  name: string;
  industry: string | null;
  created_at: string;
  changed_at: string | null;
};

export async function fetchAdvertisers(): Promise<Advertiser[]> {
  return apiFetch("/advertisers");
}

export async function createAdvertiser(data: { name: string; industry?: string | null }) {
  return apiFetch("/advertisers", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateAdvertiser(id: number, data: { name?: string; industry?: string | null }) {
  return apiFetch(`/advertisers/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteAdvertiser(id: number) {
  return apiFetch(`/advertisers/${id}`, { method: "DELETE" });
}

/* ---------------------------------------------------------- */
// Ad Contracts (Admin)
/* ---------------------------------------------------------- */
export type AdContract = {
  id: number;
  name: string;
  customer_id: string;
  customer_name?: string;
  advertiser_id: number;
  start_date: string | null;
  end_date: string | null;
  pricing_model: string | null;
  currency: string | null;
  billing_period: string | null;
  status: string | null;
  revenue_share: string | null;
  contact_person: string | null;
  language: string | null;
  created_at?: string;
  updated_at?: string | null;
};

export async function fetchContractsByAdvertiser(advertiserId: number): Promise<AdContract[]> {
  return apiFetch(`/advertisers/${advertiserId}/contracts`);
}

export async function createContract(
  advertiserId: number,
  data: {
    name: string;
    customer_id: string;
    status?: string | null;
    pricing_model?: string | null;
    currency?: string | null;
    billing_period?: string | null;
    revenue_share?: string | null;
    contact_person?: string | null;
    language?: string | null;
    start_date?: string | null;
    end_date?: string | null;
  }
) {
  return apiFetch(`/advertisers/${advertiserId}/contracts`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateContract(
  advertiserId: number,
  contractId: number,
  data: Partial<{
    name: string;
    customer_id: string;
    status: string | null;
    pricing_model: string | null;
    currency: string | null;
    billing_period: string | null;
    revenue_share: string | null;
    contact_person: string | null;
    language: string | null;
    start_date: string | null;
    end_date: string | null;
  }>
) {
  return apiFetch(`/advertisers/${advertiserId}/contracts/${contractId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteContract(advertiserId: number, contractId: number) {
  return apiFetch(`/advertisers/${advertiserId}/contracts/${contractId}`, { method: "DELETE" });
}

/* ---------------------------------------------------------- */
// Campaigns (Admin)
/* ---------------------------------------------------------- */
export type Campaign = {
  id: number;
  name: string;
  contract_id: number;
  start_date: string | null;
  end_date: string | null;
  play_goal: string | null;
  dayparting: string | null;
  status: string | null;
  created_at?: string;
  updated_at?: string | null;
};

export async function fetchCampaignsByContract(contractId: number): Promise<Campaign[]> {
  return apiFetch(`/contracts/${contractId}/campaigns`);
}

export async function createCampaign(
  contractId: number,
  data: {
    name: string;
    play_goal?: string | null;
    dayparting?: string | null;
    status?: string | null;
    start_date?: string | null;
    end_date?: string | null;
  }
) {
  return apiFetch(`/contracts/${contractId}/campaigns`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateCampaign(
  contractId: number,
  campaignId: number,
  data: Partial<{
    name: string;
    play_goal: string | null;
    dayparting: string | null;
    status: string | null;
    start_date: string | null;
    end_date: string | null;
  }>
) {
  return apiFetch(`/contracts/${contractId}/campaigns/${campaignId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteCampaign(contractId: number, campaignId: number) {
  return apiFetch(`/contracts/${contractId}/campaigns/${campaignId}`, { method: "DELETE" });
}

/* ---------------------------------------------------------- */
// Campaign Players (Admin)
/* ---------------------------------------------------------- */
export type CampaignPlayerOption = { sn: string; location?: string };

export async function fetchCampaignPlayers(campaignId: number): Promise<{
  players: CampaignPlayerOption[];
  assigned: string[];
}> {
  return apiFetch(`/campaigns/${campaignId}/players`);
}

export async function setCampaignPlayers(campaignId: number, player_sns: string[]) {
  return apiFetch(`/campaigns/${campaignId}/players`, {
    method: "PUT",
    body: JSON.stringify({ player_sns }),
  });
}

/* ---------------------------------------------------------- */
// Campaign Ads (Admin)
/* ---------------------------------------------------------- */
export type CampaignAdOption = {
  id: number;
  file_name?: string;
  name?: string;
  /** Dauer in Millisekunden */
  duration?: number | null;
};

export async function fetchCampaignAds(campaignId: number): Promise<{
  assigned: { ad_id: number; weight?: number; play_goal_override?: string | null }[];
  available: CampaignAdOption[];
}> {
  return apiFetch(`/campaigns/${campaignId}/ads`);
}

export async function setCampaignAds(
  campaignId: number,
  ads: { ad_id: number; weight?: number; play_goal_override?: string | null }[]
) {
  return apiFetch(`/campaigns/${campaignId}/ads`, {
    method: "PUT",
    body: JSON.stringify({ ads }),
  });
}

/* ---------------------------------------------------------- */
// Users
/* ---------------------------------------------------------- */
export async function fetchUsersByCustomer(customerId: string): Promise<User[]> {
  return apiFetch(`/customers/${customerId}/users`);
}

export async function createUser(
  customerId: string,
  username: string,
  password: string,
  role: string,
  assignedPlayers: string[] = []
): Promise<User> {
  return apiFetch(`/customers/${customerId}/users`, {
    method: "POST",
    body: JSON.stringify({ username, password, role, assignedPlayers }),
  });
}

export async function deleteUser(customerId: string, userId: number) {
  return apiFetch(`/customers/${customerId}/users/${userId}`, {
    method: "DELETE",
  });
}

export async function resetUserPassword(customerId: string, userId: number, password: string) {
  return apiFetch(`/customers/${customerId}/users/${userId}/reset`, {
    method: "POST",
    body: JSON.stringify({ password }),
  });
}

export async function updateUser(
  customerId: string,
  userId: number,
  updates: { username?: string; role?: string; password?: string; assignedPlayers?: string[] }
): Promise<User> {
  return apiFetch(`/customers/${customerId}/users/${userId}`, {
    method: "PATCH",
    body: JSON.stringify(updates),
  });
}

/* ---------------------------------------------------------- */
// Schedules
/* ---------------------------------------------------------- */
// Zeitplan für einen Player abrufen
export async function fetchSchedulesForPlayer(playerSN: string): Promise<ScheduleEntry[]> {
  return apiFetch(`/players/${playerSN}/schedules`);
}

// Zeitplaneintrag erstellen
export async function createScheduleEntry(
  playerSN: string,
  day_of_week: number,
  start_time: string,
  feed_id: string,
  feed_external_name: string,
  specific_date?: string | null
): Promise<ScheduleEntry> {
  return apiFetch(`/players/${playerSN}/schedules`, {
    method: "POST",
    body: JSON.stringify({ day_of_week, start_time, feed_id, feed_external_name, specific_date }),
  });
}

// Zeitplaneintrag löschen
export async function deleteScheduleEntry(playerSN: string, scheduleId: number) {
  return apiFetch(`/players/${playerSN}/schedules/${scheduleId}`, {
    method: "DELETE",
  });
}

// Zeitplaneintrag aktualisieren
export async function updateScheduleEntry(
  playerSN: string,
  scheduleId: number,
  updates: { day_of_week?: number; start_time?: string; feed_id?: string; feed_external_name?: string; specific_date?: string | null }
): Promise<ScheduleEntry> {
  return apiFetch(`/players/${playerSN}/schedules/${scheduleId}`, {
    method: "PATCH",
    body: JSON.stringify(updates),
  });
}

// Zeitplan aktivieren
export async function activateSchedule(playerId: string) {
  const res = await fetch(`${API_URL}/activateSchedule`, {
    method: "POST",
    headers: { 
      "Content-Type": "application/json",
      "Authorization": `Bearer ${localStorage.getItem("token")}`
    },
    body: JSON.stringify({ playerId }),
  });
  if (!res.ok) {
    handleUnauthorized(res);
    let errorBody: any;
    try {
      errorBody = await res.json();
    } catch (e) {
      errorBody = await res.text();
    }
    throw errorBody;
  }
  return res.json();
}

// Zeitplan deaktivieren
export async function deactivateSchedule(playerId: string) {
  const res = await fetch(`${API_URL}/deactivateSchedule`, {
    method: "POST",
    headers: { 
      "Content-Type": "application/json",
      "Authorization": `Bearer ${localStorage.getItem("token")}`
    },
    body: JSON.stringify({ playerId }),
  });
  if (!res.ok) {
    handleUnauthorized(res);
    let errorBody: any;
    try {
      errorBody = await res.json();
    } catch (e) {
      errorBody = await res.text();
    }
    throw errorBody;
  }
  return res.json();
}

// Player-Informationen abrufen (inkl. content_schedule_status und Schedule-Hash für aktuell/veraltet)
export type PlayerInfo = {
  sn: string;
  location: string;
  customer_id: number;
  time_zone?: string | null;
  content_schedule_status?: string | null;
  content_schedule_status_updated_at?: string | null;
  content_schedule_hash?: string | null;
  content_schedule_hash_applied?: string | null;
  content_schedule_hash_current?: string | null;
  ad_schedule_status?: string | null;
  ad_schedule_status_updated_at?: string | null;
};

export async function getPlayerInfo(playerSN: string): Promise<PlayerInfo> {
  return apiFetch(`/players/${playerSN}/info`);
}

/** Werbe-Status für Zeitplanung: "active" = Werbung aktiv, "draft" = Werbung möglich */
export async function getPlayerAdvertisingStatus(playerSN: string): Promise<{ status: "active" | "draft" } | null> {
  const res = await fetch(`${API_URL}/players/${playerSN}/advertising-status`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  if (res.status === 204 || res.status === 404) return null;
  if (!res.ok) throw new Error("advertising-status failed");
  const data = await res.json();
  return data.status === "active" || data.status === "draft" ? { status: data.status } : null;
}

/* ---------------------------------------------------------- */
// Content Config (rc_config)
/* ---------------------------------------------------------- */
export type ContentConfig = {
  [key: string]: any; // Dynamische Felder, außer landscape
};

// Alle rc_config Einträge abrufen (gefiltert nach landscape)
export async function fetchContentConfig(): Promise<ContentConfig[]> {
  return apiFetch(`/content-config`);
}

// Alle rc_config Einträge abrufen (gefiltert nach landscape, aber ohne type-Filter)
export async function fetchAllContentConfig(): Promise<ContentConfig[]> {
  return apiFetch(`/content-config?allTypes=true`);
}

// rc_config Eintrag aktualisieren
export async function updateContentConfig(id: number | string, updates: Partial<ContentConfig>): Promise<ContentConfig> {
  return apiFetch(`/content-config/${id}`, {
    method: "PATCH",
    body: JSON.stringify(updates),
  });
}

// rc_config Eintrag erstellen
export async function createContentConfig(data: Partial<ContentConfig>): Promise<ContentConfig> {
  return apiFetch(`/content-config`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// rc_config Eintrag löschen
export async function deleteContentConfig(id: number) {
  return apiFetch(`/content-config/${id}`, {
    method: "DELETE",
  });
}

// Thumbnail-Bild abrufen (URL) - für direkte Verwendung in img-Tags (funktioniert nicht mit Auth)
export function getThumbnailUrl(filename: string): string {
  if (!filename) return "";
  const encodedFilename = encodeURIComponent(filename);
  return `${API_URL}/content-config/thumbnail/${encodedFilename}`;
}

// Thumbnail-Bild als Blob-URL abrufen (mit Authentifizierung)
export async function getThumbnailBlobUrl(filename: string): Promise<string | null> {
  if (!filename) return null;
  
  try {
    const token = localStorage.getItem('token');
    const encodedFilename = encodeURIComponent(filename);
    const response = await fetch(`${API_URL}/content-config/thumbnail/${encodedFilename}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      handleUnauthorized(response);
      return null;
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Fehler beim Laden des Thumbnails:', error);
    return null;
  }
}

// Liste aller verfügbaren Thumbnails abrufen
export async function fetchThumbnails(): Promise<string[]> {
  return apiFetch(`/content-config/thumbnails`);
}

// Thumbnail hochladen
export async function uploadThumbnail(file: File): Promise<{ filename: string; message: string }> {
  const formData = new FormData();
  formData.append('thumbnail', file);
  
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}/content-config/upload-thumbnail`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    handleUnauthorized(response);
    const error = await response.json();
    throw new Error(error.message || 'Fehler beim Hochladen des Thumbnails');
  }

  return response.json();
}

// QR-Codes (Zielordner ../../mediaserver/qr_codes)
export async function getQrCodeBlobUrl(filename: string): Promise<string | null> {
  if (!filename) return null;

  try {
    const token = localStorage.getItem('token');
    const encodedFilename = encodeURIComponent(filename);
    const response = await fetch(`${API_URL}/content-config/qr-code/${encodedFilename}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      handleUnauthorized(response);
      return null;
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Fehler beim Laden des QR-Codes:', error);
    return null;
  }
}

export async function fetchQrCodes(): Promise<string[]> {
  return apiFetch(`/content-config/qr-codes`);
}

export async function uploadQrCode(file: File): Promise<{ filename: string; message: string }> {
  const formData = new FormData();
  formData.append('qr_code', file);

  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}/content-config/upload-qr-code`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    handleUnauthorized(response);
    const error = await response.json();
    throw new Error(error.message || 'Fehler beim Hochladen des QR-Codes');
  }

  return response.json();
}

// System-Icons (System-Modus, Zielordner SYSTEM_ICON_DIR)
export async function fetchSystemIcons(): Promise<string[]> {
  return apiFetch(`/content-config/system-icons`);
}

export async function getSystemIconBlobUrl(filename: string): Promise<string | null> {
  if (!filename) return null;

  try {
    const token = localStorage.getItem('token');
    const encodedFilename = encodeURIComponent(filename);
    const response = await fetch(`${API_URL}/content-config/system-icon/${encodedFilename}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      handleUnauthorized(response);
      return null;
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Fehler beim Laden des System-Icons:', error);
    return null;
  }
}

export async function uploadSystemIcon(file: File): Promise<{ filename: string; message: string }> {
  const formData = new FormData();
  formData.append('systemIcon', file);

  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}/content-config/upload-system-icon`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    handleUnauthorized(response);
    const error = await response.json();
    throw new Error(error.message || 'Fehler beim Hochladen des System-Icons');
  }

  return response.json();
}

// Media-Datei als Blob-URL abrufen (mit Authentifizierung). dir: 'ads' → Pfad ../../mediaserver/ads
export async function getMediaBlobUrl(filename: string, options?: { dir?: 'ads' }): Promise<string | null> {
  if (!filename) return null;
  const query = options?.dir === 'ads' ? '?dir=ads' : '';
  try {
    const token = localStorage.getItem('token');
    const encodedFilename = encodeURIComponent(filename);
    const response = await fetch(`${API_URL}/content-config/media/${encodedFilename}${query}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      handleUnauthorized(response);
      
      // Versuche, die Fehlermeldung vom Server zu extrahieren
      let errorMessage = 'Media-Datei nicht gefunden.';
      let errorData: any = {};
      try {
        errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (e) {
        // Wenn JSON-Parsing fehlschlägt, verwende Standard-Fehlermeldung
      }
      
      // Fehler als Exception werfen, damit handleViewMedia die Meldung anzeigen kann
      // Füge zusätzliche Informationen hinzu (similarFiles, etc.)
      const error = new Error(errorMessage) as any;
      if (errorData.similarFiles) {
        error.similarFiles = errorData.similarFiles;
      }
      if (errorData.requestedFile) {
        error.requestedFile = errorData.requestedFile;
      }
      throw error;
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Fehler beim Laden der Media-Datei:', error);
    // Fehler weiterwerfen, damit handleViewMedia die Meldung anzeigen kann
    throw error;
  }
}

// Liste aller verfügbaren Media-Dateien abrufen (mit Dateigröße)
export interface MediaFileInfo {
  filename: string;
  size: number;
}

// dir: 'ads' → Liste aus ../../mediaserver/ads
export async function fetchMediaFiles(options?: { dir?: 'ads' }): Promise<MediaFileInfo[]> {
  const query = options?.dir === 'ads' ? '?dir=ads' : '';
  return apiFetch(`/content-config/media${query}`);
}

// Media-Datei hochladen. options.dir: 'ads' → Ziel ../../mediaserver/ads
export async function uploadMedia(file: File, options?: { dir?: 'ads' }): Promise<{ filename: string; message: string }> {
  const formData = new FormData();
  formData.append('media', file);
  const query = options?.dir === 'ads' ? '?dir=ads' : '';
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}/content-config/upload-media${query}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    handleUnauthorized(response);
    const error = await response.json();
    throw new Error(error.message || 'Fehler beim Hochladen der Media-Datei');
  }

  return response.json();
}

// rc_config-Daten für mehrere file_names abrufen (mit Priorität für serial_number)
export async function fetchContentConfigByFileNames(fileNames: string[], playerSN: string): Promise<ContentConfig[]> {
  return apiFetch(`/content-config/by-file-names`, {
    method: 'POST',
    body: JSON.stringify({ file_names: fileNames, player_sn: playerSN }),
  });
}

// Player-Content im Asset Pool löschen
export async function clearPlayerContent(playerSN: string): Promise<{ status: string }> {
  return apiFetch(`/clear-player-content`, {
    method: 'POST',
    body: JSON.stringify({ player_sn: playerSN }),
  });
}